/* https://brandplatform-20-qa.stgr.cloud/#/Molecules/Page-log */
import { expressQuerySelector } from "../../common/html";

export interface IPageLogResult {
	logStyle: PageLogStyle;
	description: string;
}
export enum PageLogStyle {
	None,
	Error,
	WarningBlue,
	WarningOrange,
	Success,
}

export interface IPageLog {
	readonly updateMessageAndStyle: (logStyle: PageLogStyle, description: string, overrideScrollTo?: boolean) => void;
	readonly hideLog: () => void;
	readonly scrollTo: () => void;
}

export function createPageLog(containerEl: HTMLElement) {
	const iconEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-top-message-icon", true);
	const descriptionEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-top-message-description", true);

	const getClassesByPrefix = (el: HTMLElement, prefix: string) => {
		const classes: string[] = [].slice.call(el.classList);
		return classes.filter((c) => c.indexOf(prefix) > -1);
	};

	const LOG_PREFIX = "m-page-log--";
	const ICON_BG_CIRCLE_PREFIX = "v2-a-icon--background-circle-";

	const getLogIconAndType = (logStyle: PageLogStyle) => {
		switch (logStyle) {
			case PageLogStyle.Error:
				return {
					type: "m-page-log--error",
					iconBackgroundCircle: ICON_BG_CIRCLE_PREFIX + "red",
					icon: "/svg/close-symbol.svg#close-symbol",
					iconTestId: "close-symbol",
				};
			case PageLogStyle.Success:
				return {
					type: "m-page-log--validation",
					iconBackgroundCircle: ICON_BG_CIRCLE_PREFIX + "green",
					icon: "/svg/checkmark-symbol.svg#checkmark-symbol",
					iconTestId: "checkmark-symbol",
				};
			case PageLogStyle.WarningBlue:
				return {
					type: "m-page-log--info-blue",
					iconBackgroundCircle: ICON_BG_CIRCLE_PREFIX + "blue",
					icon: "/svg/info-symbol.svg#info-symbol",
					iconTestId: "info-symbol",
				};
			case PageLogStyle.WarningOrange:
				return {
					type: "m-page-log--info-orange",
					iconBackgroundCircle: ICON_BG_CIRCLE_PREFIX + "orange",
					icon: "/svg/info-symbol.svg#info-symbol",
					iconTestId: "info-symbol",
				};
			default:
				return {
					type: "",
					iconBackgroundCircle: "",
					icon: "",
					iconTestId: "",
				};
		}
	};

	const showLog = () => containerEl.classList.remove("u-hide");

	const hideLog = () => containerEl.classList.add("u-hide");

	const scrollTo = () => window.scrollTo({ top: getOffsetTop(containerEl), behavior: "smooth" });

	const getOffsetTop = (offsetElement: HTMLElement) => {
		let offsetTop = 0;
		let elem: HTMLElement | undefined = offsetElement;
		do {
			if (elem.offsetTop > 0) {
				offsetTop += elem.offsetTop;
			}

			elem = elem.offsetParent as HTMLElement | undefined;
		} while (elem);
		return offsetTop;
	};

	const updateMessageAndStyle = (logStyle: PageLogStyle, description: string, overrideScrollTo = false) => {
		const disableScroll = description === descriptionEl.innerHTML;
		containerEl.classList.remove(...getClassesByPrefix(containerEl, LOG_PREFIX));
		iconEl.classList.remove(...getClassesByPrefix(iconEl, ICON_BG_CIRCLE_PREFIX));

		const { type, iconBackgroundCircle, icon, iconTestId } = getLogIconAndType(logStyle);
		containerEl.classList.add(type);
		iconEl.classList.add(iconBackgroundCircle);
		iconEl.dataset.test = iconTestId;
		const useEl = expressQuerySelector<HTMLElement>(iconEl, "use", true);
		if (useEl.getAttribute("href") !== icon) useEl.setAttribute("href", icon);

		descriptionEl.innerHTML = description;
		showLog();
		if (!disableScroll || overrideScrollTo) scrollTo();
	};

	return {
		updateMessageAndStyle,
		hideLog,
		scrollTo,
	} as IPageLog;
}
